import {dimensionExportSize} from '../upload/export-size';

// the print units as the dimensionUnit names of dimensionExportSize ( upload/export-size.js )
const PRINT_UNITS = { mm: 'mm', cm: 'cm', in: 'inch' };


class RenderUtil {

    static getProductOption(name, options){

        for (let i = 0; i < options.length; i++) {
            const option = options[i];

            // find matching option and evaluate
            if(option.name === name){
                return option;
            }
            // support for nested options
            else if(option.options && option.options.length > 0){
                let nestedOption = this.getProductOption(name, option.options);
                // only return if found - otherwise we have to check all other potential options
                if(nestedOption != null){
                    return nestedOption;
                }
            }
        }

        return null;
    }

    /*
      NEW: shared color resolution for option connected layer color params (params.optionColor /
      optionTextShadowColor / optionTextStrokeColor).
      - color picker: option.value IS the color code
      - value-formula: derived option.value is used directly (dynamic colors, e.g. contrast color based on another option)
      - color swatch: code is defined on the selected optionValue (optionValue.color, pattern via optionValue.img)
      Returns { color, img } or null if nothing is selected / resolvable - callers decide which
      params to apply (img is only set for swatches).
    */
    static resolveOptionColor(productOption){
        if(!productOption || !productOption.value){
            return null;
        }

        // color picker OR derived value - the option value is the color code itself
        // (value-formula can return any type - only a string is usable as a color)
        if(productOption.type === 'color' || (productOption.type === 'value-formula' && typeof productOption.value === 'string')){
            return { color: productOption.value };
        }

        // color swatch - code is defined on the optionValue obj
        if(productOption.values && productOption.values.length){
            let optionValues = productOption.values.filter(v => v.value === productOption.value);

            if(optionValues.length){
                let optionValue = optionValues[0];
                return { color: optionValue.color, img: optionValue.img };
            }
        }

        return null;
    }

    static hasEditLayer(editorMode, editLayer){
        return editorMode && editLayer && editLayer !== RenderUtil.editLayer;
    }

    static isEditLayer(layer, editorMode, editLayer){
        if (editorMode) {

            // optional  filter
            if (editLayer) {
                // exact match ||  // hacky child layer support  ( startsWith not supported in IE - but this is only used in editorMode )
                if(layer.name === editLayer || layer.name.startsWith(editLayer + '_')){
                    return true;
                }

                // filtered out
                return false;
            }

            return true;
        }

        return false;
    }

    static isCropLayer(layer, cropMode, cropLayer){
        if (cropMode && layer.name === cropLayer) {
            return true;
        }

        return false;
    }

    static hasRefRect(layer){
        // NEW: refRect only active in combination with auto*   (this allows to keep the ui flag when auto* is enabled again)
        // INFO: refRect is the master switch - refArea consumers REQUIRE refRect: true (the resolved area
        //       geometry is materialized into the regular ref* params - see product-preview._materializeArea)
        return layer.params.refRect && (layer.params.autoScale || layer.params.autoPos);
    }

    static usesRefArea(layer){
        // NEW: layer follows an area layer by key - except while a crop write-back override is active
        //      (_cropRefArea, upload-renderer): the crop-derived inline geometry behaves like an inline refRect
        return !!(layer.params.refArea && !layer._cropRefArea);
    }

    /*
      NEW: area layers linked to a dimension option (docs/layer-area.md, "Print size from a dimension option")
      An area with `optionDimension` takes the customer's width x height (in its print unit `physicalUnit`) as its print
      size, and its box takes that ratio: fitted into the configured box and centred in it. Without a size (no value yet,
      hidden by its conditions, outside the option's min / max) the area keeps its configured box and its own print size.
    */

    // the configured box with the same defaults as the area editor box ( AreaRenderer._getGeometry )
    static areaBox(params, config){
        return {
            x: typeof params.x !== 'undefined' ? params.x : 50, // IMPORTANT: allow 0 in x/y
            y: typeof params.y !== 'undefined' ? params.y : 50,
            width: params.width || config.width - 100,
            height: params.height || config.height - 100,
            angle: typeof params.angle !== 'undefined' ? params.angle : 0
        };
    }

    /*
      the largest box of ratio (width / height) inside the box, centred in it. A rotated box keeps its angle: x / y is the
      rotated top-left corner (fabric's left / top with the default origin), so the centring offset rotates with it.
    */
    static fitBox(box, ratio){
        let width = box.width;
        let height = box.width / ratio;
        if(height > box.height){
            height = box.height;
            width = box.height * ratio;
        }
        const dx = (box.width - width) / 2;
        const dy = (box.height - height) / 2;
        const rad = (box.angle || 0) * Math.PI / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        return {
            x: box.x + dx * cos - dy * sin,
            y: box.y + dx * sin + dy * cos,
            width: width,
            height: height,
            angle: box.angle || 0
        };
    }

    /*
      the params of an area linked to a dimension option for the entered size: the box fitted to its ratio and the size as
      the print size; null when the area is not linked or the option has no size (the area keeps its own params)
    */
    static linkedAreaParams(params, dimensionOption, config){
        if(!params.optionDimension){
            return null;
        }
        const size = dimensionExportSize(dimensionOption, PRINT_UNITS[params.physicalUnit]);
        if(!size){
            return null;
        }
        const box = this.fitBox(this.areaBox(params, config), size.w / size.h);
        return Object.assign({}, params, box, { physicalWidth: size.w, physicalHeight: size.h, physicalUnit: size.u });
    }

    // the params an area resolves to for its consumers and exports: the linked ones of the last render, else its own
    static areaParams(layer){
        return layer._linkedParams || layer.params;
    }

    /*
      NEW: overlay semantics - the layer must COVER its ref box instead of fitting inside it.
      Explicit via the backend autoScaleOverlay setting, implicit for crop uploads: crop only
      makes sense when the image covers the crop area, so the backend does not require the flag
      (it is only injected temporarily while crop mode is active - overlay-renderer). Consumers
      deciding between "fit inside" and "cover" containment must use this instead of reading
      params.autoScaleOverlay directly (same rule as the object:moving containment `|| isCrop`).
    */
    static isOverlay(layer, params = layer.params){
        return !!(params.autoScaleOverlay || (layer.type === 'upload' && params.crop));
    }

    /*
      NEW: upload scale box MODE (docs/layer-area.md section 10) - a manually positioned upload
      (autoPos=false, x/y set in the backend or by dragging in editor mode) where params.width/height
      define the box autoScale fits the image INTO, decoupled from the ref geometry that governs
      movement bounds, clipPath and crop. width/height are optional: every consumer falls back
      per-field to the ref dimensions, so without them the mode is a no-op on the storefront while
      the editor resize controls already work - the first resize is what creates the dimensions.
      EXCLUDED: overlay layers (crop uploads / autoScaleOverlay - must cover, not fit)
    */
    static hasScaleBox(layer, params = layer.params){
        return layer.type === 'upload' && params.autoScale && !params.autoPos
            && !RenderUtil.isOverlay(layer, params);
    }

    static getRefDimensions(layer, context){
        if(RenderUtil.hasRefRect(layer)){
            // use configured ref dimensions || fallback to same default as in _getRefRect
            return { width: layer.params.refWidth || context.config.width - 100 , height: layer.params.refHeight || context.config.height - 100 };
        } else {
            // default full canvas
            return { width: context.config.width , height: context.config.height };
        }
    }

    static getRefCoordinates(layer, context){
        if(RenderUtil.hasRefRect(layer)){
            // use configured ref coordinates - fallback to same default as in _getRefRect
            return {
                x: typeof layer.params.refX !== 'undefined' ? layer.params.refX : 50, y: typeof layer.params.refY !== 'undefined' ? layer.params.refY: 50,
                angle: typeof layer.params.refAngle !== 'undefined' ? layer.params.refAngle : 0
            };
        } else {
            if(!layer.params.autoPos){  // when auto positioning is disabled
                // custom reference support
                return { x: layer.params.x | 0, y: layer.params.y | 0, angle: 0 };
            }
            // default top/left
            return { x: 0, y: 0, angle: 0 };
        }
    }

    static getDimensions(object, rotated = false){
        if(rotated){
            return {
                height: object.getScaledWidth(),
                width: object.getScaledHeight()
            };
        } else {
            return {
                width: object.getScaledWidth(),
                height: object.getScaledHeight()
            };
        }

    }

    // NEW: axis aligned bounds of the ref rect - rotated refs use the rotated bounding box
    static getRefBounds(layer, context){
        let coordinates = RenderUtil.getRefCoordinates(layer, context);
        let dimensions = RenderUtil.getRefDimensions(layer, context);

        if(coordinates.angle){
            let rotated = new fabric.Rect({
                left: coordinates.x,
                top: coordinates.y,
                width: dimensions.width,
                height: dimensions.height,
                angle: coordinates.angle
            }).getBoundingRect();

            return { left: rotated.left, top: rotated.top, width: rotated.width, height: rotated.height };
        }

        return { left: coordinates.x, top: coordinates.y, width: dimensions.width, height: dimensions.height };
    }

    /*
      Constrain a resize around Fabric's fixed transform origin (the opposite corner, or the center
      for centered scaling). Corner names are local to the object, not screen-space box edges.
      Use live bounds: aCoords can still describe the previous mouse move during object:scaling.
    */
    static constrainScale(object, bounds, transform){
        let rect = object.getBoundingRect(true, true);
        let { originX = 'center', originY = 'center' } = transform || {};
        let anchor = object.getPointByOrigin(originX, originY);
        let right = bounds.left + bounds.width;
        let bottom = bounds.top + bounds.height;

        let fitScale = Math.min(1, bounds.width / rect.width, bounds.height / rect.height);
        let scale = fitScale;
        let epsilon = 1e-7; // canvas pixels, only to ignore floating-point noise at a fixed edge

        // Include boundary roundoff here too; otherwise a valid fixed anchor can take the fallback
        // path and jump. Negative available space within epsilon is treated as zero below.
        // A valid starting placement keeps its anchor throughout the gesture. If a programmatic
        // resize starts outside the box, fall back to fitting and translating it back inside.
        if(anchor.x >= bounds.left - epsilon && anchor.x <= right + epsilon &&
            anchor.y >= bounds.top - epsilon && anchor.y <= bottom + epsilon){
            let edges = [ // [object extent from anchor, available space] for left/right/top/bottom
                [anchor.x - rect.left, anchor.x - bounds.left],
                [rect.left + rect.width - anchor.x, right - anchor.x],
                [anchor.y - rect.top, anchor.y - bounds.top],
                [rect.top + rect.height - anchor.y, bottom - anchor.y]
            ];

            edges.forEach(([distance, available]) => {
                // At a rotated fixed edge, roundoff can turn a zero extent into ~1e-13.
                // Dividing zero available space by that extent collapses the scale to zero,
                // then Fabric divides by zero on the next mouse move. Only constrain real overflow.
                if(distance > available + epsilon){
                    scale = Math.min(scale, Math.max(0, available) / distance);
                }
            });
        }

        // An outside placement can have its anchor exactly ON the boundary with all of its
        // extent facing out. No positive scale fits around that anchor: fit and translate instead.
        if(scale === 0){
            scale = fitScale;
        }

        if(scale < 1){
            // Preserve the current X/Y ratio, including non-uniform scaling, and restore the
            // anchor AFTER changing size. Positioning with the old bounds causes rotated jumps.
            object.scaleX *= scale;
            object.scaleY *= scale;
            object.setPositionByOrigin(anchor, originX, originY);
            rect = object.getBoundingRect(true, true);
        }

        let min = { x: bounds.left, y: bounds.top };
        let max = { x: right - rect.width, y: bottom - rect.height };
        RenderUtil.clampObject(object, rect, min, max);
        object.setCoords();
    }

    /*
      Preserve the existing resize behavior for overlays/cropped uploads. Their initial bounds may
      extend outside the ref box, so applying the fit-inside constraint would abruptly shrink them.
      This compatibility path intentionally retains the old buffers and scale/position ordering;
      changing overlay sizing (including the ability to shrink below the box) is a separate concern.
    */
    static constrainOverlayScale(object, bounds, transform){
        let min = { x: bounds.left, y: bounds.top };
        let max = { x: bounds.left + bounds.width, y: bounds.top + bounds.height };

        let boundingRect = object.getBoundingRect(true, true);

        let scale = null;

        let buffer = 20;
        if(transform && transform.corner){
            if(transform.corner == 'tr'){
                min.x -= buffer;
                max.y += buffer;
            } else if(transform.corner == 'tl'){
                max.x += buffer;
                max.y += buffer;
            } else if(transform.corner == 'br'){
                min.x -= buffer;
                min.y -= buffer;

            } else if(transform.corner == 'bl'){
                max.x += buffer;
                min.y -= buffer;
            }
        }

        if (Math.floor(boundingRect.left) < min.x){

            let maxWidth = object.aCoords.tr.x - min.x;

            scale = maxWidth / (boundingRect.width / object.scaleX);

            if(object.angle !== 0){
                object.setPositionByOrigin({ x: min.x + (boundingRect.width / 2), y: object.getCenterPoint().y }, 'center', 'center');
            } else {
                object.left = min.x;
            }

        } else if(Math.floor(boundingRect.left + boundingRect.width) > max.x){
            let maxWidth = max.x - boundingRect.left;
            scale = maxWidth / (boundingRect.width / object.scaleX);

            if(object.angle !== 0){
                object.setPositionByOrigin({ x: (max.x - maxWidth) + (boundingRect.width / 2), y: object.getCenterPoint().y }, 'center', 'center');
            } else {
                object.left = max.x - maxWidth;
            }
        }

        if(Math.floor(boundingRect.top) < min.y){

            let maxHeight = (boundingRect.top + boundingRect.height) - min.y;

            scale = maxHeight / (boundingRect.height / object.scaleY);

            if(object.angle !== 0){
                object.setPositionByOrigin({ x: object.getCenterPoint().x, y: min.y + (boundingRect.height / 2) }, 'center', 'center');
            } else {
                object.top = min.y;
            }

        } else if(Math.floor(boundingRect.top + boundingRect.height) > max.y){
            let maxHeight = max.y - boundingRect.top;
            scale = maxHeight / (boundingRect.height / object.scaleY);

            if(object.angle !== 0){
                object.setPositionByOrigin({ x: object.getCenterPoint().x, y: (max.y - maxHeight) + (boundingRect.height / 2) }, 'center', 'center');
            } else {
                object.top = max.y - maxHeight;
            }
        }

        if(scale != null){
            object.scaleX = scale;
            object.scaleY = scale;

            object.setCoords();

        }
    }

    /*
      NEW: box containment shared by the object:moving handler (product-preview) and the refArea render
      projection (LayerRenderer._validateArea) - derives min/max (incl. the autoScaleOverlay variant
      + the _interactiveScaled demotion) and clamps the object
      bounds = axis aligned box (getRefBounds), ref = unrotated ref dimensions (getRefDimensions)
    */
    static containObject(object, layer, boundingRect, bounds, ref, allowOverlay){
        // per axis: skip overlay where the customer scaled the object smaller than the ref box
        // (a portrait scaled narrower but still taller keeps the y overlay, contain would pin it to an edge)
        let scaled = allowOverlay && layer._interactiveScaled;
        let overlayX = allowOverlay && !(scaled && boundingRect.width < ref.width);
        let overlayY = allowOverlay && !(scaled && boundingRect.height < ref.height);

        let min = { x: bounds.left, y: bounds.top };
        let max = { x: bounds.left + bounds.width, y: bounds.top + bounds.height };

        // overlay: allow moving outside of the box as long as the box stays fully overlayed
        // default: subtract the object dimensions from max to force the object to be inside the box
        if(overlayX){
            min.x -= boundingRect.width - ref.width;
            max.x -= ref.width;
        } else {
            max.x -= boundingRect.width;
        }
        if(overlayY){
            min.y -= boundingRect.height - ref.height;
            max.y -= ref.height;
        } else {
            max.y -= boundingRect.height;
        }

        RenderUtil.clampObject(object, boundingRect, min, max);
    }

    /*
      NEW: center snap during drag (product-preview object:moving, enabled per area layer via
      params.snap - the matching center lines are part of the area guide, params.displayCenterH/V) -
      snaps the object's bounding rect center onto the bounds center per enabled axis when within
      threshold (canvas px). translates rigidly like clampObject (the bbox is symmetric about the
      object center, so bbox center == object center for any rotation) and keeps boundingRect in
      sync because the moving handler passes the same rect on to the containment afterwards
    */
    static snapToCenter(object, boundingRect, bounds, threshold, axes = { x: true, y: true }){
        let snapped = { x: false, y: false };

        let deltaX = (bounds.left + bounds.width / 2) - (boundingRect.left + boundingRect.width / 2);
        let deltaY = (bounds.top + bounds.height / 2) - (boundingRect.top + boundingRect.height / 2);

        if(axes.x && Math.abs(deltaX) <= threshold){
            object.left += deltaX;
            boundingRect.left += deltaX;
            snapped.x = true;
        }

        if(axes.y && Math.abs(deltaY) <= threshold){
            object.top += deltaY;
            boundingRect.top += deltaY;
            snapped.y = true;
        }

        if(snapped.x || snapped.y){
            object.setCoords();
        }

        return snapped;
    }

    /*
      NEW: clamp the object into min/max - translates by the bounding rect delta (rigid translation:
      equivalent to setPositionByOrigin center math for rotated objects, the bbox is symmetric
      about the center). min has priority when the object is larger than the box (min.x > max.x)
    */
    static clampObject(object, boundingRect, min, max){
        let left = boundingRect.left;
        let top = boundingRect.top;

        if(boundingRect.left < min.x){
            left = min.x;
        } else if(boundingRect.left > max.x){
            left = max.x;
        }

        if(boundingRect.top < min.y){
            top = min.y;
        } else if(boundingRect.top > max.y){
            top = max.y;
        }

        if(left !== boundingRect.left || top !== boundingRect.top){
            object.left += left - boundingRect.left;
            object.top += top - boundingRect.top;
            object.setCoords();
        }
    }

    static center(object, dimensions, refCoordinates, refDimensions) {
        this.centerHorizontal(object, dimensions, refCoordinates, refDimensions);
        this.centerVertical(object, dimensions, refCoordinates, refDimensions);
    }

    static centerHorizontal(object, dimensions, refCoordinates, refDimensions) {
        let x = refCoordinates.x + ( refDimensions.width / 2  ) - ( dimensions.width / 2 );
        object.left = parseFloat(x.toFixed(2));
    }

    static centerVertical(object, dimensions, refCoordinates, refDimensions) {
        let y = refCoordinates.y + ( refDimensions.height / 2  ) - ( dimensions.height / 2 );
        object.top = parseFloat(y.toFixed(2));
    }

}

RenderUtil.editLayer = '_'; // NOOP edit layer (excludes all - set when no layer is selected in backend)

export default RenderUtil;
